import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRouteMatch } from 'react-router-dom';

import { useApplicationState } from '@atlas/core';
import { NotificationTopic, TransportManager } from '@atlas/transport';

import { bonusStatuses, bonusTypes } from 'common/constants';
import {
  checkDepositBonusTypeFits,
  findLatestActivatedBonus,
  findTransportMatchedBonus,
  getBonusMinDeposit,
  normalizeBonusWithFinanceData,
} from 'src/common/utils/depositBonusUtils';
import useBonuses from 'hooks/useBonuses';
import { sortBonusesByAvailabilityAndAmount } from 'src/common/utils/sortBonusesByAvailabilityAndAmount';

export const DepositBonusContext = createContext({
  activateBonus: () => {},
  activatedBonus: null,
  activeBonuses: [],
  amountToGetBonus: 0,
  bonusToShow: null,
  bonusesList: [],
  bonusesToShow: [],
  depositAmount: undefined,
  depositBonuses: [],
  depositBonusesToShow: [],
  intendedBonusRef: null,
  isBonusesDisabled: false,
  isLoading: false,
  selectedBonus: null,
  setDepositAmount: () => {},
  setSelectedBonus: () => {},
  wallet: {},
});

export const useDepositBonusContext = () => useContext(DepositBonusContext);

const { NO_DEPOSIT, RELOAD_NO_DEPOSIT } = bonusTypes;
const { ACTIVATED, IN_PROGRESS } = bonusStatuses;

export const useCreateDepositBonusContext = () => {
  const { params: { paymentId: paymentMethodId } = {} } = useRouteMatch();

  const paymentMethod = useMemo(
    () => (paymentMethodId == null ? null : { id: paymentMethodId }),
    [paymentMethodId],
  );

  const { activateBonus, activeBonuses, bonuses, isLoading, wallet } = useBonuses();
  const applicationState = useApplicationState();

  const [depositAmount, setDepositAmount] = useState();
  const [selectedBonus, setSelectedBonus] = useState(null);
  const [isBonusesDisabled, setIsBonusesDisabled] = useState(false);
  const [depositCompleteBonus, setDepositCompleteBonus] = useState(null);
  const [depositCompletePaymentMethodId, setDepositCompletePaymentMethodId] = useState(null);
  const [
    hasPaymentMethodChangedSinceDepositComplete,
    setHasPaymentMethodChangedSinceDepositComplete,
  ] = useState(false);
  const intendedBonusRef = useRef(null);
  const latestSelectedBonusRef = useRef(null);

  const { currency } = wallet;
  const country = applicationState?.account?.country || null;

  const depositBonuses = useMemo(() => {
    const activeDepositBonuses = activeBonuses
      .filter(bonus => {
        const { bonus: { bonusType } = {}, status } = bonus;

        return checkDepositBonusTypeFits(bonusType) && status === ACTIVATED;
      })
      .map(bonus => normalizeBonusWithFinanceData(bonus, currency))
      .sort(sortBonusesByAvailabilityAndAmount);

    const availableDepositBonuses = bonuses
      .filter(bonus => checkDepositBonusTypeFits(bonus.bonusType))
      .map(bonus => normalizeBonusWithFinanceData(bonus, currency))
      .sort(sortBonusesByAvailabilityAndAmount);

    return [...activeDepositBonuses, ...availableDepositBonuses];
  }, [bonuses, activeBonuses, currency]);

  const isBonusAllowed = useCallback(
    bonus => {
      const normalizeBonus = bonus?.bonus ? bonus.bonus : bonus;

      return (
        !normalizeBonus?.basicLimit?.notAllowedPaymentMethods?.includes(
          paymentMethod?.id?.toString(),
        ) && !normalizeBonus?.basicLimit?.notAllowedCountries?.includes(country)
      );
    },
    [country, paymentMethod?.id],
  );

  const normalizeBonusForDisplay = useCallback(
    bonus => {
      const normalizedBonus = normalizeBonusWithFinanceData(bonus, currency);

      return {
        ...normalizedBonus,
        disabled: !isBonusAllowed(normalizedBonus),
      };
    },
    [currency, isBonusAllowed],
  );

  const bonusToShow = depositBonuses[0];

  const internalActivatedBonus = useMemo(() => {
    const latestActivatedBonus = findLatestActivatedBonus({
      activeBonuses,
      isMatchingBonus: bonus => checkDepositBonusTypeFits(bonus?.bonusType),
    });

    if (!latestActivatedBonus) {
      return undefined;
    }

    return normalizeBonusForDisplay(latestActivatedBonus);
  }, [activeBonuses, normalizeBonusForDisplay]);

  const findBonusByTransportPayload = useCallback(
    ({ bonusId, bonusInstanceId } = {}) => {
      return findTransportMatchedBonus({
        activeBonuses,
        bonuses,
        bonusId,
        bonusInstanceId,
        currency,
        isMatchingBonus: bonus => checkDepositBonusTypeFits(bonus?.bonusType),
      });
    },
    [activeBonuses, bonuses, currency],
  );

  const visibleDepositCompleteBonus =
    depositCompleteBonus && !hasPaymentMethodChangedSinceDepositComplete
      ? depositCompleteBonus
      : null;

  const activatedBonus = useMemo(() => {
    if (visibleDepositCompleteBonus) {
      return visibleDepositCompleteBonus;
    }

    return internalActivatedBonus ?? null;
  }, [internalActivatedBonus, visibleDepositCompleteBonus]);

  const activatedBonusProductTypes = useMemo(() => {
    const productTypes = activeBonuses.reduce((accumulator, activeBonus) => {
      const bonusType = activeBonus?.bonus?.bonusType;
      const productType = activeBonus?.bonus?.productType;

      if (
        activeBonus?.status !== ACTIVATED ||
        !checkDepositBonusTypeFits(bonusType) ||
        !productType ||
        accumulator.includes(productType)
      ) {
        return accumulator;
      }

      return [...accumulator, productType];
    }, []);

    if (
      hasPaymentMethodChangedSinceDepositComplete &&
      depositCompleteBonus?.productType &&
      !productTypes.includes(depositCompleteBonus.productType)
    ) {
      return [...productTypes, depositCompleteBonus.productType];
    }

    return productTypes;
  }, [activeBonuses, depositCompleteBonus, hasPaymentMethodChangedSinceDepositComplete]);

  const shouldFilterBonusListByActivatedTypes =
    !visibleDepositCompleteBonus && hasPaymentMethodChangedSinceDepositComplete;

  const depositBonusesToShow = useMemo(() => {
    const typesToHide = activeBonuses
      .filter(
        active =>
          active.status === IN_PROGRESS &&
          (active.bonus?.bonusType === NO_DEPOSIT ||
            active.bonus?.bonusType === RELOAD_NO_DEPOSIT ||
            active.bonus?.productType === 'CASINO' ||
            active.bonus?.productType === 'SPORTSBOOK'),
      )
      .map(active => active.bonus?.productType);

    const filteredBonuses = typesToHide.length
      ? depositBonuses.filter(bonus => !typesToHide.includes(bonus.productType))
      : depositBonuses;

    const filteredByActivatedBonusType = shouldFilterBonusListByActivatedTypes
      ? filteredBonuses.filter(bonus => !activatedBonusProductTypes.includes(bonus.productType))
      : filteredBonuses;

    const bonusesToShow = filteredByActivatedBonusType.map(bonus => ({
      ...normalizeBonusForDisplay(bonus),
    }));

    return [
      ...bonusesToShow.filter(bonus => !bonus.disabled),
      ...bonusesToShow.filter(bonus => bonus.disabled),
    ];
  }, [
    activatedBonusProductTypes,
    activeBonuses,
    depositBonuses,
    normalizeBonusForDisplay,
    shouldFilterBonusListByActivatedTypes,
  ]);

  const applyLockedBonusState = useCallback(bonus => {
    const nextBonus = bonus?.disabled ? null : bonus;

    setSelectedBonus(nextBonus);
    setIsBonusesDisabled(true);
    intendedBonusRef.current = nextBonus;
    latestSelectedBonusRef.current = nextBonus;
  }, []);

  useEffect(() => {
    const handleDepositComplete = transportMatchedBonus => {
      const completedBonus =
        transportMatchedBonus ||
        selectedBonus ||
        latestSelectedBonusRef.current ||
        intendedBonusRef.current ||
        internalActivatedBonus ||
        null;

      intendedBonusRef.current = completedBonus;
      setSelectedBonus(completedBonus);
      setDepositCompleteBonus(completedBonus);
      setDepositCompletePaymentMethodId(paymentMethod?.id ?? null);
      setHasPaymentMethodChangedSinceDepositComplete(false);
    };

    const unsubscribe = TransportManager.getInstance().subscribe(
      {
        notificationTopic: NotificationTopic.BONUS_INSTANCE_STATUS_CHANGED,
      },
      data => {
        const completedBonus = findBonusByTransportPayload(data);

        handleDepositComplete(completedBonus);
      },
    );

    return unsubscribe;
  }, [findBonusByTransportPayload, internalActivatedBonus, paymentMethod?.id, selectedBonus]);

  useEffect(() => {
    if (
      depositCompletePaymentMethodId == null ||
      hasPaymentMethodChangedSinceDepositComplete ||
      paymentMethod?.id == null
    ) {
      return;
    }

    if (depositCompletePaymentMethodId !== paymentMethod.id) {
      setHasPaymentMethodChangedSinceDepositComplete(true);
    }
  }, [
    depositCompletePaymentMethodId,
    hasPaymentMethodChangedSinceDepositComplete,
    paymentMethod?.id,
  ]);

  useEffect(() => {
    const lockedBonus = visibleDepositCompleteBonus || internalActivatedBonus;

    if (lockedBonus) {
      applyLockedBonusState(lockedBonus);

      return;
    }

    const firstAvailableBonus = depositBonusesToShow.find(bonus => !bonus.disabled) || null;
    const getAllowedBonus = bonus =>
      depositBonusesToShow.find(item => item.id === bonus?.id && !item.disabled) || null;

    if (intendedBonusRef.current && !getAllowedBonus(intendedBonusRef.current)) {
      intendedBonusRef.current = null;
    }

    setSelectedBonus(prev => {
      if (prev) {
        return getAllowedBonus(prev) || firstAvailableBonus;
      }

      if (intendedBonusRef.current || !firstAvailableBonus) {
        return null;
      }

      return firstAvailableBonus;
    });
  }, [
    applyLockedBonusState,
    internalActivatedBonus,
    depositBonusesToShow,
    visibleDepositCompleteBonus,
  ]);

  useEffect(() => {
    if (visibleDepositCompleteBonus || internalActivatedBonus) {
      setIsBonusesDisabled(true);

      return;
    }

    const currentAmount = Number(depositAmount);
    const activeOrSelected = selectedBonus || internalActivatedBonus;
    const bonusToCheck = intendedBonusRef.current || activeOrSelected;

    if (!bonusToCheck) {
      return;
    }

    const minDeposit = getBonusMinDeposit(bonusToCheck, currency);

    if (depositAmount === '' || depositAmount === null || depositAmount === undefined) {
      setIsBonusesDisabled(false);

      return;
    }

    if (currentAmount > 0 && currentAmount < minDeposit) {
      if (selectedBonus) {
        intendedBonusRef.current = selectedBonus;
        setSelectedBonus(null);
      }
      setIsBonusesDisabled(true);
    } else if (currentAmount >= minDeposit) {
      if (intendedBonusRef.current) {
        setSelectedBonus(intendedBonusRef.current);
        intendedBonusRef.current = null;
      }
      setIsBonusesDisabled(false);
    }
  }, [currency, depositAmount, internalActivatedBonus, selectedBonus, visibleDepositCompleteBonus]);

  const handleSetSelectedBonus = useCallback(
    bonus => {
      const isSameAsVisibleActivatedBonus =
        bonus?.id != null &&
        (bonus.id === activatedBonus?.id || bonus.id === visibleDepositCompleteBonus?.id);

      if (isSameAsVisibleActivatedBonus) {
        latestSelectedBonusRef.current = bonus;
        intendedBonusRef.current = bonus;
        setSelectedBonus(bonus);

        return;
      }

      if (!hasPaymentMethodChangedSinceDepositComplete) {
        setDepositCompleteBonus(null);
        setDepositCompletePaymentMethodId(null);
        setHasPaymentMethodChangedSinceDepositComplete(false);
      }

      if (!bonus) {
        intendedBonusRef.current = null;
        latestSelectedBonusRef.current = null;
        setSelectedBonus(null);

        return;
      }

      if (isBonusesDisabled) {
        intendedBonusRef.current = bonus;
        latestSelectedBonusRef.current = bonus;
        setSelectedBonus(null);
      } else {
        intendedBonusRef.current = null;
        latestSelectedBonusRef.current = bonus;
        setSelectedBonus(bonus);
      }
    },
    [
      activatedBonus?.id,
      hasPaymentMethodChangedSinceDepositComplete,
      isBonusesDisabled,
      visibleDepositCompleteBonus?.id,
    ],
  );

  const amountToGetBonus = useMemo(() => {
    const amount = Number(depositAmount);

    if (!depositAmount || Number.isNaN(amount)) {
      return 0;
    }

    const bonus = internalActivatedBonus || selectedBonus || intendedBonusRef.current;

    if (!bonus) {
      return 0;
    }

    const minDeposit = getBonusMinDeposit(bonus, currency);

    return Math.max(minDeposit - amount, 0);
  }, [internalActivatedBonus, currency, selectedBonus, depositAmount]);

  const bonusesList = useMemo(() => {
    if (activatedBonus) {
      return [{ ...activatedBonus }];
    }

    return depositBonusesToShow;
  }, [activatedBonus, depositBonusesToShow]);

  return useMemo(
    () => ({
      activateBonus,
      activatedBonus,
      activeBonuses,
      amountToGetBonus,
      bonusToShow,
      bonusesList,
      bonusesToShow: bonusesList,
      depositAmount,
      depositBonuses,
      depositBonusesToShow,
      intendedBonusRef,
      isBonusesDisabled,
      isLoading,
      selectedBonus,
      setDepositAmount,
      setSelectedBonus: handleSetSelectedBonus,
      wallet,
    }),
    [
      activateBonus,
      activatedBonus,
      activeBonuses,
      amountToGetBonus,
      bonusToShow,
      bonusesList,
      depositAmount,
      depositBonuses,
      depositBonusesToShow,
      handleSetSelectedBonus,
      isBonusesDisabled,
      isLoading,
      selectedBonus,
      wallet,
    ],
  );
};
